export interface SingleInstanceOptions {
  onSecondInstance?: () => void; // Callback triggered by second tab
  redirectTo?: string; // Redirect page for second tab
  close?: boolean; // Whether second tab auto closes
  lockTimeout?: number; // Lock timeout (ms)
  heartbeatInterval?: number; // Heartbeat update lock interval (ms)
  restrictedPaths?: string[]; // Restricted path list, only enable single instance check under these paths
}

export class SingleInstanceGuard {
  private bc: BroadcastChannel;
  private isMainTab = true;
  private options: SingleInstanceOptions;
  private tabId: string;
  private lockKey = '__single_instance_lock__';
  private heartbeatTimer?: number;
  private static instance: SingleInstanceGuard;

  constructor(options: SingleInstanceOptions = {}) {
    this.options = {
      close: true,
      lockTimeout: 5000,
      heartbeatInterval: 2000,
      ...options,
    };
    this.tabId = Math.random().toString(36).substr(2, 9);
    this.bc = new BroadcastChannel('single-instance');
    SingleInstanceGuard.instance = this;
    this.start();
  }

  /** Get global instance */
  static getInstance(): SingleInstanceGuard | undefined {
    return SingleInstanceGuard.instance;
  }

  private start() {
    // Check if current path is in restricted list
    if (!this.isPathRestricted()) {
      return;
    }

    this.setupBroadcastChannel();
    this.checkOtherTabs();
    this.setupHeartbeat();
    this.setupBeforeUnload();
  }

  /** Check if current path is in restricted list */
  private isPathRestricted(): boolean {
    const { restrictedPaths } = this.options;

    // If no restricted paths configured, don't enable single instance check
    if (!restrictedPaths || restrictedPaths.length === 0) {
      return false;
    }

    const currentPath = window.location.pathname;
    return restrictedPaths.some((path) => currentPath.startsWith(path));
  }

  /** BroadcastChannel message listener */
  private setupBroadcastChannel() {
    this.bc.onmessage = (ev) => {
      const { type, tabId } = ev.data || {};
      if (tabId === this.tabId) return; // Ignore own messages

      if (type === 'check' && this.isMainTab) {
        this.bc.postMessage({ type: 'exist', tabId: this.tabId });
      }

      if (type === 'exist') {
        // New tab detected main tab exists, prevent opening
        this.handleSecondInstance();
      }
    };
  }

  /** Broadcast check to other tabs */
  private checkOtherTabs() {
    setTimeout(() => {
      this.bc.postMessage({ type: 'check', tabId: this.tabId });
    }, 50);

    // Wait for a while, if no response from other tabs, this is the first tab
    // If response received, it will be handled in setupBroadcastChannel
    setTimeout(() => {
      if (this.isMainTab) {
        // This is main tab, listen for route changes
        this.setupRouteChangeListener();
      }
    }, 200);
  }

  /** Listen for route changes, cleanup resources when leaving restricted paths */
  private setupRouteChangeListener() {
    window.addEventListener('popstate', () => {
      if (!this.isPathRestricted()) {
        this.cleanup();
      }
    });
  }

  /** Heartbeat: update lock timestamp */
  private setupHeartbeat() {
    if (!this.isMainTab) return;
    const now = Date.now();
    localStorage.setItem(
      this.lockKey,
      JSON.stringify({ id: this.tabId, ts: now }),
    );

    this.heartbeatTimer = window.setInterval(() => {
      localStorage.setItem(
        this.lockKey,
        JSON.stringify({ id: this.tabId, ts: Date.now() }),
      );
    }, this.options.heartbeatInterval);
  }

  /** Release lock when tab closes */
  private setupBeforeUnload() {
    window.addEventListener('beforeunload', () => {
      const lockRaw = localStorage.getItem(this.lockKey);
      if (lockRaw) {
        const lock = JSON.parse(lockRaw);
        if (lock.id === this.tabId) {
          localStorage.removeItem(this.lockKey);
        }
      }
      this.bc.close();
      if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    });
  }

  /** Handle second tab - prevent opening */
  private handleSecondInstance() {
    const { onSecondInstance, redirectTo } = this.options;

    onSecondInstance?.();

    // Disable page operations
    document.body.innerHTML =
      '<h2>This app is already running in another tab</h2>';

    if (redirectTo) {
      setTimeout(() => {
        location.href = redirectTo;
      }, 1000);
    }
  }

  /** Cleanup resources */
  private cleanup() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    try {
      this.bc.close();
    } catch (e) {
      // BroadcastChannel may already be closed
    }
  }

  /** Check if a tab already has restricted path open */
  hasRestrictedPathOpened(): boolean {
    const { restrictedPaths } = this.options;

    // If no restricted paths configured, return false
    if (!restrictedPaths || restrictedPaths.length === 0) {
      return false;
    }

    // Check if there's a lock in localStorage
    const lockRaw = localStorage.getItem(this.lockKey);
    if (!lockRaw) {
      return false;
    }

    try {
      const lock = JSON.parse(lockRaw);
      // Check if lock is expired
      const now = Date.now();
      const lockTimeout = this.options.lockTimeout || 5000;
      if (now - lock.ts > lockTimeout) {
        // Lock expired, cleanup
        localStorage.removeItem(this.lockKey);
        return false;
      }
      return true;
    } catch (e) {
      return false;
    }
  }
}
